import React, { useEffect, useRef, useState } from 'react';

import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { Alert, Box, Button, Typography } from '@mui/material';
import chromeIcon from 'assets/icons/chrome.svg';
import HeaderComp from 'components/elements/HeadingComponent';
import StepProgress from 'components/ProgressBar';
import { userTypes } from 'config/config';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { RootState } from 'redux/store';

import { parseContractData } from '../utils';
import StepOne, { StepOneRef } from './StepOne';
import StepThree from './StepThree';
import DynamicForm, { DynamicFormRef } from './StepTwo';

const sidebarFields = [
  { type: 'signature', label: 'Signature field' },
  { type: 'text', label: 'Text field' },
  { type: 'date', label: 'Date field' },
];

interface createContractInterface {
  levels: number;
  yearToRetain: number;
  creatorRole: string;
  contractName: string;
  signees?: Array<{
    name: string;
    email: string;
    signeeType: string;
    color: string;
    level: number;
    fields: Array<{
      type: string;
      label: string;
    }>;
  }>;
  levelData?: Array<{
    level: number;
    signees: Array<{
      name: string;
      email: string;
      signeeType: string;
      color: string;
      level: number;
      fields: Array<{
        type: string;
        label: string;
      }>;
    }>;
  }>;
  pdfFile?: File | null;
  selectedPdf?: string | null;
  formData?: any[];
  dynamicFormValues?: any[];
}

const NewContract = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { customerId, corporateId, role, employeeId } = useSelector(
    (state: RootState) => state.auth
  );
  const folderId: string | null =
    role === userTypes.individual ? customerId : corporateId;

  const [currentStep, setCurrentStep] = useState(0);
  const [contractDetails, setContractDetails] =
    useState<createContractInterface>({
      levels: 0,
      yearToRetain: 0,
      creatorRole: '',
      contractName: '',
    });
  const [validationError, setValidationError] = useState('');
  const formRefs = useRef<(DynamicFormRef | null)[]>([]);
  const stepOneRef = useRef<StepOneRef>(null);

  useEffect(() => {
    if (contractDetails.levels) {
      formRefs.current = Array.from(
        { length: contractDetails.levels },
        (_, i) => formRefs.current[i] || null
      );
    }
  }, [contractDetails.levels]);

  const handleSubmitAll = async () => {
    // First, check for global email duplicates across all levels
    const allEmails: string[] = [];
    const levelEmails: { level: number; emails: string[] }[] = [];

    // Collect all emails from all levels
    formRefs.current.forEach((ref, index) => {
      if (ref) {
        const emails = ref.getAllEmails();
        levelEmails.push({ level: index + 1, emails });
        allEmails.push(...emails);
      }
    });

    // Check for global duplicates
    const uniqueEmails = new Set(allEmails);
    if (allEmails.length !== uniqueEmails.size) {
      // Find which levels have duplicates
      const duplicateEmails = allEmails.filter(
        (email, index) => allEmails.indexOf(email) !== index
      );
      const uniqueDuplicates = Array.from(new Set(duplicateEmails));

      // Find which levels contain the duplicate emails
      const duplicateInfo = uniqueDuplicates.map((email) => {
        const levels = levelEmails
          .filter((level) => level.emails.includes(email))
          .map((level) => level.level);
        return `${email} (Levels: ${levels.join(', ')})`;
      });

      setValidationError(
        `Duplicate emails found across levels: ${duplicateInfo.join(', ')}. Each signee must have a unique email.`
      );
      return;
    }

    // Validate all forms before proceeding
    const validationResults = formRefs.current.map((ref) =>
      ref?.validateStep()
    );

    const allValid = validationResults.every((result) => result === true);

    if (!allValid) {
      setValidationError(
        'Please fix all validation errors in all levels before proceeding to the next step.'
      );
      return;
    }

    const results = await Promise.all(
      formRefs.current.map((ref) => ref?.validateAndGetValues())
    );

    const validForms = results.filter((res: any) => res !== null);

    if (validForms.length === formRefs.current.length) {
      const data = parseContractData(validForms);
      setContractDetails((prev) => ({
        ...prev,
        signees: data.contractDetails.signees,
        levelData: data.contractDetails.levels,
        formData: validForms,
        yearToRetain: prev.yearToRetain,
        creatorRole: prev.creatorRole,
        contractName: prev.contractName,
        pdfFile: prev.pdfFile,
        selectedPdf: prev.selectedPdf,
      }));
      setValidationError(''); // Clear any previous errors
      setCurrentStep((prev) => prev + 1); // Proceed to next step
    } else {
      setValidationError(
        'Some forms have validation errors. Please fix them before proceeding.'
      );
    }
  };

  const handlePdfFileChange = (file: File | null, url: string | null) => {
    setContractDetails((prev) => ({
      ...prev,
      pdfFile: file,
      selectedPdf: url,
    }));
  };

  const handleStepOneSubmit = (data: Partial<createContractInterface>) => {
    setContractDetails((prev) => ({
      ...prev,
      ...data,
    }));
  };

  const handleStepTwoSubmit = (formData: any) => {
    setContractDetails((prev) => ({
      ...prev,
      dynamicFormValues: formData,
    }));
  };

  const handleNextStep = () => {
    setValidationError('');

    if (currentStep === 0) {
      // Validate StepOne before proceeding
      const isValid = stepOneRef.current?.validateStep();
      if (!isValid) {
        setValidationError(
          'Please fix all validation errors before proceeding to the next step.'
        );
        return;
      }
    } else if (currentStep === 1) {
      // For StepTwo, we need to validate all forms before proceeding
      handleSubmitAll();
      return; // Don't proceed to next step here, let handleSubmitAll handle it
    }

    setCurrentStep((prev) => prev + 1);
  };

  return (
    <DndProvider backend={HTML5Backend}>
      {/* add back button here */}
      <HeaderComp>
        <Typography variant="h5" fontWeight="bold">
          Create New Contract
        </Typography>
      </HeaderComp>
      <Box display="flex" justifyContent="flex-start" width="100%" mb={5}>
        <Button
          variant="text"
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />}
        >
          Back to Contracts
        </Button>
      </Box>
      <Box display="flex" justifyContent="center" width="100%">
        <Box
          border="1px solid rgb(209, 211, 215)"
          p={4}
          borderRadius={4}
          width="100%"
        >
          <StepProgress
            steps={3}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />

          {/* Step Instructions */}
          <Box
            mt={3}
            mb={3}
            sx={{
              backgroundColor: '#f8f9fa',
              p: 2,
              borderRadius: 2,
              border: '1px solid #e9ecef',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: currentStep === 0 ? '#4caf50' : '#6c757d',
                fontWeight: currentStep === 0 ? 600 : 400,
                fontSize: '0.875rem',
                mb: 0.8,
                lineHeight: 1.4,
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <span
                style={{
                  marginRight: '8px',
                  fontSize: '1.2rem',
                  color: currentStep === 0 ? '#4caf50' : '#6c757d',
                }}
              >
                •
              </span>
              Configure contract settings including number of signature levels
              and retention period.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: currentStep === 1 ? '#4caf50' : '#6c757d',
                fontWeight: currentStep === 1 ? 600 : 400,
                fontSize: '0.875rem',
                mb: 0.8,
                lineHeight: 1.4,
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <span
                style={{
                  marginRight: '8px',
                  fontSize: '1.2rem',
                  color: currentStep === 1 ? '#4caf50' : '#6c757d',
                }}
              >
                •
              </span>
              Add signees for each level and configure their signature fields.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: currentStep === 2 ? '#4caf50' : '#6c757d',
                fontWeight: currentStep === 2 ? 600 : 400,
                fontSize: '0.875rem',
                lineHeight: 1.4,
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <span
                style={{
                  marginRight: '8px',
                  fontSize: '1.2rem',
                  color: currentStep === 2 ? '#4caf50' : '#6c757d',
                }}
              >
                •
              </span>
              Upload your PDF document and place signature fields on the
              contract.
            </Typography>
          </Box>

          {/* Browser Recommendation Note */}
          <Box
            sx={{
              backgroundColor: '#e3f2fd',
              p: 2,
              borderRadius: 2,
              border: '1px solid #2196f3',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 3,
            }}
          >
            <img
              src={chromeIcon}
              alt="Google Chrome"
              style={{
                width: '20px',
                height: '20px',
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: '#1976d2',
                fontWeight: 500,
                fontSize: '0.875rem',
              }}
            >
              Please use Google Chrome browser to create contract (if loggedIn
              in different browser, please login using Google Chrome) for best
              user experience
            </Typography>
          </Box>

          <Box mt={5}>
            {currentStep === 0 && (
              <StepOne
                ref={stepOneRef}
                setContractDetails={handleStepOneSubmit}
                contractDetails={contractDetails}
              />
            )}
            {currentStep === 1 && (
              <>
                {formRefs.current.map((ref, index) => (
                  <React.Fragment key={index}>
                    <Typography variant="h6" fontWeight="bold" mb={2}>
                      Level {index + 1}
                    </Typography>
                    <DynamicForm
                      ref={(el) => {
                        formRefs.current[index] = el;
                      }}
                      setContractDetails={handleStepTwoSubmit}
                      initialData={contractDetails.formData?.[index]}
                      requiredSignees={1} // Each level requires 1 signee by default
                      levelIndex={index}
                      allLevelsData={contractDetails.formData || []}
                    />
                  </React.Fragment>
                ))}
                {/* <Box display="flex" justifyContent="center" mb={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSubmitAll}
                  >
                    Submit All
                  </Button>
                </Box> */}
              </>
            )}
            {currentStep === 2 && (
              <StepThree
                setPdfFile={handlePdfFileChange}
                signees={contractDetails.signees || []}
                initialPdf={contractDetails.selectedPdf}
                contractDetails={{
                  ...contractDetails,
                  levelData: contractDetails.levelData,
                }}
                uuid={uuid}
                folderId={folderId}
              />
            )}
          </Box>

          {validationError && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {validationError}
            </Alert>
          )}

          <Box display="flex" justifyContent="flex-end" gap={5}>
            <Button
              variant="outlined"
              onClick={() => setCurrentStep((prev) => prev - 1)}
              disabled={currentStep === 0}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              onClick={handleNextStep}
              disabled={currentStep === 2}
            >
              Next
            </Button>
          </Box>
        </Box>
      </Box>
      {/* <PDFViewerSign pdfFile={pdfFile} setPdfFile={setPdfFile} /> */}
    </DndProvider>
  );
};

export default NewContract;
